import { LoaderFunctionArgs, redirect } from 'react-router-dom';
import { api } from '@/lib/api';
import type { InsuranceConfigFormDataDto } from '@/lib/ServerApi';
import { queryClient } from '@/providers/query-client';

export type InsuranceConfigFormLoaderData = InsuranceConfigFormDataDto;

/**
 * React Router loader for `/settings/insurance-config/create` and `.../:configId/edit`.
 *
 * Fetches {@link InsuranceConfigFormDataDto} via `api.insurance.getNewConfigFormData` / `getConfigFormData`, cached under
 * `['insurance','config','form', configId, insuranceId]` (create flow uses `undefined` segments when those params are absent).
 *
 * Optional `?insuranceId=` on create prefills/locks the pending payer.
 */
export async function insuranceConfigFormLoader({ params, request }: LoaderFunctionArgs): Promise<InsuranceConfigFormLoaderData> {
    const configId = params.configId;
    const insuranceId = new URL(request.url).searchParams.get('insuranceId') ?? undefined;

    return queryClient.fetchQuery({
        queryKey: ['insurance', 'config', 'form', configId, insuranceId],
        queryFn: async () => {
            if (!configId) {
                const { data } = await api.insurance.getNewConfigFormData(insuranceId ? { insuranceId } : {});
                if (!data) {
                    throw new Error('Missing insurance config form data');
                }
                return data;
            }
            const { data } = await api.insurance.getConfigFormData(configId);

            if (!data) {
                return redirect('/settings/insurance-config');
            }
            return data;
        },
    });
}
